import { ApiInputType } from "@/core/common/types/api";
import { classNames } from "@/core/utils/classname-utils";
import * as Slider from "@radix-ui/react-slider";
import React from "react";

export type DiscreteSliderInputProps = React.HTMLAttributes<HTMLDivElement> & {
  type: ApiInputType.Slider;
  id: string;
  name: React.ReactNode;
  description?: React.ReactNode;
  descriptionPosition?: "label" | "bottom";
  descriptionClassName?: string;
  label: string;
  value: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  required?: boolean;
  hideRequiredLabel?: boolean;
  onValueChange: (value: number) => void;
};

export function DiscreteSliderInput({
  id,
  name,
  description,
  descriptionPosition = "label",
  descriptionClassName = "",
  label,
  value,
  defaultValue = 0,
  min = 0,
  max = 100,
  required = false,
  hideRequiredLabel = false,
  onValueChange,
  className = "",
  ...props
}: DiscreteSliderInputProps) {
  const handleValueChange = React.useCallback(
    (values: number[]) => {
      const roundedValue = Math.round(values[0]);
      onValueChange(roundedValue);
    },
    [onValueChange],
  );

  return (
    <div className={classNames("flex flex-col gap-1.5", className)} {...props}>
      <Slider.Root
        className="relative flex items-center select-none touch-none w-full h-5"
        value={[value]}
        defaultValue={[defaultValue]}
        min={min}
        max={max}
        step={1}
        onValueChange={handleValueChange}
        aria-label="Discrete Slider"
      >
        <div className="absolute w-full flex justify-between top-1/2 -translate-y-1/2">
          {Array.from({ length: max - min + 1 }).map((_, i) => (
            <div
              key={`${id}-${i}`}
              className={classNames(
                "w-4 h-4 rounded-full relative",
                i <= value - min ? "bg-lime-500" : "bg-zinc-700",
              )}
              role="presentation"
            >
              {i === value - min && (
                <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-zinc-700 text-white text-xs px-2 py-1 rounded text-nowrap">
                  {label}
                </div>
              )}
            </div>
          ))}
        </div>
        <Slider.Track className="bg-zinc-700 relative grow rounded-full h-[3px]">
          <Slider.Range className="absolute bg-lime-500 rounded-full h-[4px]" />
        </Slider.Track>
        <Slider.Thumb
          className="block w-5 h-5 bg-white rounded-full shadow-lg hover:bg-lime-50 focus:outline-none focus:ring-2 focus:ring-lime-500"
          aria-label="Slider thumb"
        />
      </Slider.Root>

      {descriptionPosition === "bottom" && description && (
        <div className={classNames("text-sm text-zinc-400", descriptionClassName)}>
          {description}
        </div>
      )}
    </div>
  );
}
